import { css } from 'aphrodite';
import * as React from 'react';
import { SelectButton } from '../../../web/components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxTrigger,
	ComboboxValue,
} from '../../../web/components-v2/Combobox';
import { styles } from './styles';

const UNCATEGORIZED_CATEGORY_ID = '__support-category-uncategorized__';

interface CategoryPickerCategory {
	id: string;
	name: string;
	description?: string;
	aiGuidance?: string;
	color?: string;
	isActive: boolean;
	usageCount?: number;
}

interface CategoryPickerOption extends CategoryPickerCategory {
	isUncategorized?: boolean;
}

interface CategoryPickerProps {
	categories: CategoryPickerCategory[];
	selectedId?: string;
	onSelect: (id: string | null) => void;
}

const uncategorizedOption: CategoryPickerOption = {
	id: UNCATEGORIZED_CATEGORY_ID,
	name: 'Uncategorized',
	isActive: true,
	isUncategorized: true,
};

export const CategoryPicker: React.FC<CategoryPickerProps> = ({ categories, selectedId, onSelect }) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [searchValue, setSearchValue] = React.useState('');

	const options = React.useMemo<CategoryPickerOption[]>(() => {
		return [
			uncategorizedOption,
			...categories.filter(category => category.isActive).sort((a, b) => (b.usageCount ?? 0) - (a.usageCount ?? 0)),
		];
	}, [categories]);

	const selected = categories.find(category => category.id === selectedId) ?? uncategorizedOption;

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			setSearchValue('');
		}
	};

	const handleValueChange = (option: CategoryPickerOption | null) => {
		if (!option) return;
		onSelect(option.isUncategorized ? null : option.id);
		setSearchValue('');
		setIsOpen(false);
	};

	return (
		<div className={css(styles.categoryPicker)}>
			<Combobox
				items={options}
				value={selected}
				inputValue={searchValue}
				onInputValueChange={setSearchValue}
				open={isOpen}
				onOpenChange={handleOpenChange}
				onValueChange={value => handleValueChange(value as CategoryPickerOption | null)}
				isItemEqualToValue={(item, value) => (item as CategoryPickerOption).id === (value as CategoryPickerOption).id}
				itemToStringValue={item => (item as CategoryPickerOption).id}
				itemToStringLabel={item => (item as CategoryPickerOption).name}
			>
				<ComboboxTrigger
					render={({ className, ...props }) => {
						void className;
						return (
							<SelectButton
								{...props}
								type='button'
								aria-label='Support category'
								styles={[styles.categoryPickerTrigger]}
							>
								<ComboboxValue>
									{(value: CategoryPickerOption | null) => <CategoryPickerLabel category={value ?? selected} />}
								</ComboboxValue>
							</SelectButton>
						);
					}}
				/>
				<ComboboxContent align='end' styles={[styles.categoryPickerDropdown]}>
					<div className={css(styles.categoryPickerSearchWrapper)}>
						<ComboboxInput placeholder='Search categories...' showTrigger={false} />
					</div>
					<ComboboxEmpty>No matching categories.</ComboboxEmpty>
					<ComboboxList styles={[styles.categoryPickerList]}>
						{(option: CategoryPickerOption) => (
							<ComboboxItem key={option.id} value={option} title={option.aiGuidance || option.description || undefined}>
								<ComboboxItemRadioIndicator />
								<CategoryPickerLabel category={option} />
							</ComboboxItem>
						)}
					</ComboboxList>
				</ComboboxContent>
			</Combobox>
		</div>
	);
};

function CategoryPickerLabel({ category }: { category: CategoryPickerOption }) {
	return (
		<span className={css(styles.categoryPickerValue)}>
			{category.color && !category.isUncategorized ? (
				<span className={css(styles.categoryPickerSwatch)} style={{ backgroundColor: category.color }} />
			) : null}
			<span className={css(styles.categoryPickerName)}>{category.name}</span>
		</span>
	);
}
